/**
 * Form model for the add/edit tile dialog (S6): flat TanStack-Form values,
 * defaults from an existing tile (edit mode), suggestion application (shape
 * autopilot), and assembly back into a persisted `DashboardTile`.
 */
import type {
  AggregationFunction,
  ChartConfig,
  ChartType,
  ColumnTypeInfo,
} from '../../../charts/types/chartTypes';
import { labelOverridesForKeys } from '../../../charts/utils/chartLabels';
import { resolveChartColumnRef } from '../../../charts/utils/chartColumnRefs';
import type {
  DashboardEngineId,
  DashboardTile,
  DashboardTileType,
  FilterBinding,
  NoteTile,
  StatCompare,
  StatFormat,
  TileThreshold,
} from '../../types/dashboardTypes';
import type { TileSuggestion } from '../../utils/shapeSuggest';

export type RefLineRow = { value: number; label: string; severity: 'none' | 'warning' | 'critical' };

export type AddTileFormValues = {
  title: string;
  tileType: DashboardTileType | '';
  connectionId: string;
  /**
   * The database the tile's query runs against ('' = none picked). Form-owned
   * so the dialog's database picker, the VQE schema pulls, previews, and the
   * saved `tile.database` all read ONE value; the dialog seeds it via
   * `addTileDatabaseDefault.ts` (configured → sqlite 'main' → first listed).
   */
  database: string;
  refreshMode: 'manual' | 'interval';
  refreshSeconds: number;
  resultSetIndex: number;
  chartType: ChartType;
  chartX: string;
  chartY: Array<{ column: string; aggregation: AggregationFunction }>;
  chartSeries: string;
  chartShowLegend: boolean;
  chartShowGrid: boolean;
  referenceLines: RefLineRow[];
  statValueColumn: string;
  statFormatKind: StatFormat['kind'];
  statPercentScale: 'fraction' | 'percent';
  statCurrencyCode: string;
  statDurationUnit: 'ms' | 's' | 'min';
  statCompareKind: StatCompare['kind'];
  statCompareColumn: string;
  statCompareTarget: number;
  statSparkline: boolean;
  statSparkTime: string;
  statSparkValue: string;
  statThresholds: TileThreshold[];
  tableRowLimit: number;
  tableDensity: 'compact' | 'comfortable';
  pivotRow: string;
  pivotColumn: string;
  pivotValue: string;
  pivotAgg: AggregationFunction;
  /** '' = auto-detect (the map panel picks the first spatial column). */
  mapGeometryColumn: string;
  mapColorBy: string;
  noteMarkdown: string;
  /** Carried from a template (S7); no UI field — round-trips through the form. */
  emptyStateCopy: string;
};

export const REFRESH_INTERVAL_OPTIONS = [5, 15, 30, 60, 300, 900, 3600] as const;

const BASE_DEFAULTS: AddTileFormValues = {
  title: '',
  tileType: '',
  connectionId: '',
  database: '',
  refreshMode: 'manual',
  refreshSeconds: 30,
  resultSetIndex: 0,
  chartType: 'bar',
  chartX: '',
  chartY: [{ column: '', aggregation: 'sum' }],
  chartSeries: '',
  chartShowLegend: false,
  chartShowGrid: true,
  referenceLines: [],
  statValueColumn: '',
  statFormatKind: 'number',
  statPercentScale: 'fraction',
  statCurrencyCode: 'USD',
  statDurationUnit: 'ms',
  statCompareKind: 'none',
  statCompareColumn: '',
  statCompareTarget: 0,
  statSparkline: false,
  statSparkTime: '',
  statSparkValue: '',
  statThresholds: [],
  tableRowLimit: 100,
  tableDensity: 'compact',
  pivotRow: '',
  pivotColumn: '',
  pivotValue: '',
  pivotAgg: 'sum',
  mapGeometryColumn: '',
  mapColorBy: '',
  noteMarkdown: '',
  emptyStateCopy: '',
};

export function formDefaultsFromTile(tile: DashboardTile | null): AddTileFormValues {
  if (!tile) return { ...BASE_DEFAULTS };
  const values: AddTileFormValues = { ...BASE_DEFAULTS, title: tile.title, tileType: tile.tileType };
  if (tile.tileType === 'note') return { ...values, noteMarkdown: tile.markdown };
  values.connectionId = tile.connectionId;
  values.database = tile.database ?? '';
  values.refreshMode = tile.refresh.mode;
  if (tile.refresh.mode === 'interval') values.refreshSeconds = tile.refresh.seconds;
  values.resultSetIndex = tile.resultSetIndex ?? 0;
  values.emptyStateCopy = tile.emptyStateCopy ?? '';
  switch (tile.tileType) {
    case 'chart':
      return {
        ...values,
        chartType: tile.chartConfig.type,
        chartX: tile.chartConfig.xAxis.column,
        chartY: tile.chartConfig.yAxis.map((y) => ({
          column: y.column,
          aggregation: y.aggregation ?? 'sum',
        })),
        chartSeries: tile.chartConfig.series?.column ?? '',
        chartShowLegend: tile.chartConfig.showLegend,
        chartShowGrid: tile.chartConfig.showGrid,
        referenceLines: (tile.referenceLines ?? []).map((line) => ({
          value: line.value,
          label: line.label ?? '',
          severity: line.severity ?? 'none',
        })),
      };
    case 'stat':
      return {
        ...values,
        statValueColumn: tile.valueColumn ?? '',
        statFormatKind: tile.format.kind,
        statPercentScale: tile.format.kind === 'percent' ? tile.format.scale : 'fraction',
        statCurrencyCode: tile.format.kind === 'currency' ? tile.format.code : 'USD',
        statDurationUnit: tile.format.kind === 'duration' ? tile.format.unit : 'ms',
        statCompareKind: tile.compare.kind,
        statCompareColumn: tile.compare.kind === 'previous-period' ? tile.compare.column : '',
        statCompareTarget: tile.compare.kind === 'target' ? tile.compare.value : 0,
        statSparkline: Boolean(tile.sparkline),
        statSparkTime: tile.sparkline?.timeColumn ?? '',
        statSparkValue: tile.sparkline?.valueColumn ?? '',
        statThresholds: tile.thresholds ?? [],
      };
    case 'table':
      return { ...values, tableRowLimit: tile.rowLimit, tableDensity: tile.density };
    case 'pivot':
      return {
        ...values,
        pivotRow: tile.rowColumn,
        pivotColumn: tile.columnColumn,
        pivotValue: tile.valueColumn,
        pivotAgg: tile.aggregation,
      };
    case 'map':
      return {
        ...values,
        mapGeometryColumn: tile.geometryColumn ?? '',
        mapColorBy: tile.colorByColumn ?? '',
      };
  }
}

/**
 * Chart-picker fields (`chartX`/`chartY[].column`/`chartSeries`) re-resolved
 * against the picked preview set's columns, once that preview lands.
 * `formDefaultsFromTile` seeds these straight from the persisted
 * `ChartConfig` at dialog-open time, before any preview has run — there is
 * no column info yet to resolve a NAME-based (or whitespace-rekeyed)
 * reference against.
 *
 * Takes the CURRENT form values, not the saved tile: the preview can still
 * be running while the user edits the form (e.g. changes a Y aggregation
 * from `sum` to `avg`), and re-deriving from `tile.chartConfig` once the
 * preview completes would silently discard that edit (PR review, P2).
 * Resolving in place instead only ever touches the `column` STRING of an
 * entry whose reference doesn't already match a known key — every other
 * field (aggregation, entry order, added/removed Y entries) passes through
 * untouched. Returns `null` when there's nothing to resolve (no columns yet,
 * or every reference already resolves to itself) so the caller can skip the
 * patch and object identity stays stable for memoized consumers.
 */
export function resolveChartFormColumnValues(
  values: Pick<AddTileFormValues, 'chartX' | 'chartY' | 'chartSeries'>,
  columnInfos: readonly ColumnTypeInfo[],
): Pick<AddTileFormValues, 'chartX' | 'chartY' | 'chartSeries'> | null {
  if (columnInfos.length === 0) return null;

  const resolvedX = values.chartX ? resolveChartColumnRef(values.chartX, columnInfos) : values.chartX;

  let yChanged = false;
  const resolvedY = values.chartY.map((y) => {
    if (!y.column) return y;
    const resolved = resolveChartColumnRef(y.column, columnInfos);
    if (resolved === y.column) return y;
    yChanged = true;
    return { ...y, column: resolved };
  });

  const resolvedSeries = values.chartSeries
    ? resolveChartColumnRef(values.chartSeries, columnInfos)
    : values.chartSeries;

  if (resolvedX === values.chartX && !yChanged && resolvedSeries === values.chartSeries) {
    return null;
  }

  return {
    chartX: resolvedX,
    chartY: yChanged ? resolvedY : values.chartY,
    chartSeries: resolvedSeries,
  };
}

/** Autopilot acceptance — the form patch for a suggestion's type + auto-config. */
export function suggestionPatch(suggestion: TileSuggestion): Partial<AddTileFormValues> {
  switch (suggestion.tileType) {
    case 'chart':
      return {
        tileType: 'chart',
        chartType: suggestion.chartConfig.type,
        chartX: suggestion.chartConfig.xAxis.column,
        chartY: suggestion.chartConfig.yAxis.map((y) => ({
          column: y.column,
          aggregation: y.aggregation ?? 'sum',
        })),
        chartShowLegend: suggestion.chartConfig.showLegend,
        chartShowGrid: suggestion.chartConfig.showGrid,
      };
    case 'stat':
      return {
        tileType: 'stat',
        statValueColumn: suggestion.valueColumn ?? '',
        statSparkline: Boolean(suggestion.sparkline),
        statSparkTime: suggestion.sparkline?.timeColumn ?? '',
        statSparkValue: suggestion.sparkline?.valueColumn ?? '',
      };
    case 'pivot':
      return {
        tileType: 'pivot',
        pivotRow: suggestion.rowColumn,
        pivotColumn: suggestion.columnColumn,
        pivotValue: suggestion.valueColumn,
      };
    case 'map':
      return {
        tileType: 'map',
        mapGeometryColumn: suggestion.geometryColumn ?? '',
      };
    case 'table':
      return { tileType: 'table' };
  }
}

/** Shared by `buildTileFromForm` and the dialog's live tile preview. */
export const statFormatFromValues = (v: AddTileFormValues): StatFormat => {
  switch (v.statFormatKind) {
    case 'percent':
      return { kind: 'percent', scale: v.statPercentScale };
    case 'currency':
      return { kind: 'currency', code: v.statCurrencyCode };
    case 'duration':
      return { kind: 'duration', unit: v.statDurationUnit };
    default:
      return { kind: 'number' };
  }
};

/** Shared by `buildTileFromForm` and the dialog's live tile preview. */
export const statCompareFromValues = (v: AddTileFormValues): StatCompare => {
  if (v.statCompareKind === 'previous-period' && v.statCompareColumn) {
    return { kind: 'previous-period', column: v.statCompareColumn };
  }
  if (v.statCompareKind === 'target') return { kind: 'target', value: v.statCompareTarget };
  return { kind: 'none' };
};

/**
 * Chart config exactly as `buildTileFromForm` persists it — shared with the
 * live tile preview. `columnInfos` is optional (callers pass it when the
 * picked preview set's column shape is on hand — AddTileDialog's submit,
 * AddTilePreview's chart body) and attaches the same disambiguated `label`
 * override `ChartBuilderDialog` does, so a blank/duplicate column (e.g.
 * `SELECT id, id`) reads "Column 2" in the legend/tooltip instead of its raw
 * row key (`col_1`), and two same-named picks never collide into one chart
 * data key (chartLabels.ts).
 */
export const chartConfigFromValues = (
  v: AddTileFormValues,
  columnInfos: readonly ColumnTypeInfo[] = [],
): ChartConfig => {
  const yEntries = v.chartY.filter((y) => y.column);
  const [xLabel, ...yLabels] = labelOverridesForKeys(
    [v.chartX, ...yEntries.map((y) => y.column)],
    columnInfos,
  );
  return {
    type: v.chartType,
    xAxis: { column: v.chartX, ...xLabel },
    yAxis: yEntries.map((y, i) => ({
      column: y.column,
      aggregation: y.aggregation,
      ...yLabels[i],
    })),
    series: v.chartSeries ? { column: v.chartSeries } : undefined,
    showLegend: v.chartShowLegend,
    showGrid: v.chartShowGrid,
  };
};

/** Reference lines exactly as `buildTileFromForm` persists them — shared with the live tile preview. */
export const referenceLinesFromValues = (v: AddTileFormValues) =>
  v.referenceLines
    .filter((line) => Number.isFinite(line.value))
    .map((line) => ({
      value: line.value,
      label: line.label || undefined,
      severity: line.severity === 'none' ? undefined : line.severity,
    }));

export type BuildTileArgs = {
  values: AddTileFormValues;
  sql: string;
  /**
   * Existing tile when editing. Preserved verbatim: id, layout, source kind
   * (template/vqe keep their provenance — only the SQL text updates), and
   * every same-type field the form does not manage (`filterBindings`,
   * `alertRule`, table `columns`/`conditionalFormats`, `pageId`). A tile-type
   * CHANGE deliberately drops the old type's fields.
   */
  existing: DashboardTile | null;
  layout: { x: number; y: number; w: number; h: number };
  engine?: DashboardEngineId;
  /**
   * S8: 'vqe' when the SQL was last authored on the Visual tab — persists
   * `{ kind: 'vqe' }` provenance. Omitted/'sql' keeps the existing behavior
   * (new tiles are `kind:'sql'`; edited tiles keep their stored kind).
   */
  sourceKind?: 'sql' | 'vqe';
  /**
   * S8: cross-filter bindings derived from the VQE semantic model at save
   * time (consumed by S11). Only passed for visual-authored SQL — a
   * hand-edited query invalidates the derivation, so the dialog omits it and
   * any same-type existing bindings are preserved instead.
   */
  filterBindings?: FilterBinding[];
  /** Picked preview set's column shape — see `chartConfigFromValues`. */
  columnInfos?: readonly ColumnTypeInfo[];
};

export function buildTileFromForm(args: BuildTileArgs): DashboardTile | null {
  const { values: v, existing } = args;
  if (!v.tileType) return null;
  const sameType = existing?.tileType === v.tileType ? existing : null;
  const id = existing?.id ?? crypto.randomUUID();
  const layout = existing?.layout ?? args.layout;
  const title = v.title.trim() || `New ${v.tileType}`;
  if (v.tileType === 'note') {
    const sameNote: NoteTile | null = sameType?.tileType === 'note' ? sameType : null;
    return { ...(sameNote ?? {}), id, tileType: 'note', title, layout, markdown: v.noteMarkdown };
  }
  const sameQuery = sameType && sameType.tileType !== 'note' ? sameType : null;
  const queryBase = {
    ...(sameQuery ?? {}),
    id,
    title,
    layout,
    connectionId: v.connectionId,
    database: v.database || undefined,
    engine: args.engine ?? sameQuery?.engine,
    source:
      args.sourceKind === 'vqe'
        ? { kind: 'vqe' as const, sql: args.sql }
        : sameQuery && sameQuery.source.kind !== 'sql'
          ? { ...sameQuery.source, sql: args.sql }
          : { kind: 'sql' as const, sql: args.sql },
    filterBindings: args.filterBindings ?? sameQuery?.filterBindings,
    refresh:
      v.refreshMode === 'interval'
        ? ({ mode: 'interval', seconds: v.refreshSeconds } as const)
        : ({ mode: 'manual' } as const),
    resultSetIndex: v.resultSetIndex || undefined,
    emptyStateCopy: v.emptyStateCopy.trim() || undefined,
  };
  switch (v.tileType) {
    case 'chart':
      return {
        ...queryBase,
        tileType: 'chart',
        chartConfig: chartConfigFromValues(v, args.columnInfos),
        referenceLines: referenceLinesFromValues(v),
      };
    case 'stat':
      return {
        ...queryBase,
        tileType: 'stat',
        valueColumn: v.statValueColumn || undefined,
        format: statFormatFromValues(v),
        compare: statCompareFromValues(v),
        sparkline:
          v.statSparkline && v.statSparkTime && v.statSparkValue
            ? { timeColumn: v.statSparkTime, valueColumn: v.statSparkValue }
            : undefined,
        thresholds: v.statThresholds.length ? v.statThresholds : undefined,
      };
    case 'table':
      return { ...queryBase, tileType: 'table', rowLimit: v.tableRowLimit, density: v.tableDensity };
    case 'pivot':
      return {
        ...queryBase,
        tileType: 'pivot',
        rowColumn: v.pivotRow,
        columnColumn: v.pivotColumn,
        valueColumn: v.pivotValue,
        aggregation: v.pivotAgg,
      };
    case 'map':
      return {
        ...queryBase,
        tileType: 'map',
        geometryColumn: v.mapGeometryColumn || undefined,
        colorByColumn: v.mapColorBy || undefined,
      };
  }
}
